import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { useEffect, useRef, useState } from 'react'

import { t } from 'localization'
import { RotatingCaret } from 'shared/components'
import Button from 'shared/components/Button'
import MobileOverlay from 'shared/components/MobileOverlay'
import PDPDivider from 'shared/components/PDPDivider'
import { ProductQA } from 'shared/dataAttributes'
import { formatMoneyObject } from 'shared/lib/formatMoneyObject'
import { ISizePickerOption } from 'shared/types/ISizePickerOption'

const TP = 'productTemplate.components.ProductPicker.SizePickerMobile'

import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { useExperimentState } from 'experiments/hooks/useExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { blinkStyles } from 'productTemplate/components/ProductPicker/blinkStyles'
import { ShoppingRegionSelector } from 'productTemplate/components/ProductPicker/ShoppingRegionSelector'
import { useProductTemplateContext } from 'productTemplate/components/ProductTemplateContextProvider'
import { getProductSizeCategory } from 'productTemplate/utils/getProductSizeCategory'
import {
  getDiscountPercentage,
  getUnderRetailLabel,
} from 'productTemplate/utils/under-retail-utils'
import { SVGIcon } from 'shared/components/Icons'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { colors, fonts } from 'shared/lib'
import styled from 'styled-components'
import BuyButtonRow from './BuyButtonRow'
import {
  MobileOverlayPanel,
  MobileSizePickerModalTitle,
  SizeInputField,
  SizeLabel,
  SizeOptionTile,
  SizeOptionWrapper,
} from './ProductPickerStyles'

interface ISizePickerMobileProps {
  sizePickerOptions: ISizePickerOption[]
  handleSizeOptionClick: (option: ISizePickerOption) => void
  isButtonRowVisible: boolean
}

const SizePickerMobile = ({
  sizePickerOptions,
  handleSizeOptionClick,
  isButtonRowVisible,
}: ISizePickerMobileProps) => {
  const regionSelectorPlacement: EXPERIMENT_NAME = 'web_fc_fully_landed_pricing_copy_placement_test'
  const webFCUnderRetailExperiment: EXPERIMENT_NAME = 'web_fc_under_retail'
  const webFCAllInclusivePricingExperiment: EXPERIMENT_NAME = 'web_fc_all_inclusive_pricing_v2'
  const enableExperiments = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  const enablePricingCopyPlacementExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_FULLY_LANDED_PRICING_COPY_EXPERIMENT,
  )
  const enableUnderRetailExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_UNDER_RETAIL_EXPERIMENT,
  )
  const enableAllInclusivePricingCopyExperiment = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_ALL_INCLUSIVE_PRICING_COPY_EXPERIMENT,
  )
  const contentEl = useRef<HTMLDivElement>(null)
  const [isSelectorOpen, setIsSelectorOpen] = useState(false)
  const { activeProductModal, productTemplate, selectedSize, isApparel } =
    useProductTemplateContext()
  const { country, currencyCode } = useShoppingRegionContext()
  const experimentState = useExperimentState()

  const { newLowestPrice, sizeCategory, usedLowestPrice, localizedSpecialDisplayPriceCents } =
    productTemplate

  const handleDropdownClick = () => {
    setIsSelectorOpen(!isSelectorOpen)
  }

  const underRetailExperimentGroup =
    enableExperiments && enableUnderRetailExperiment && experimentState[webFCUnderRetailExperiment]
      ? experimentState[webFCUnderRetailExperiment].groupAssigned
      : 'control'

  const allInclusivePricingCopyExperimentGroup =
    enableExperiments &&
    enableAllInclusivePricingCopyExperiment &&
    experimentState[webFCAllInclusivePricingExperiment]
      ? experimentState[webFCAllInclusivePricingExperiment].groupAssigned
      : 'control'

  const displayAllInclusiveCopy =
    allInclusivePricingCopyExperimentGroup !== 'control' && country === 'US'

  const underRetailLabel = getUnderRetailLabel(
    underRetailExperimentGroup || '',
    getDiscountPercentage(
      localizedSpecialDisplayPriceCents?.value || 0,
      selectedSize?.new?.lowestPriceCents?.value || 0,
    ) || 0,
    country,
  )

  const getSizeLabel = (sizeCategory: string) => {
    switch (sizeCategory.toLowerCase()) {
      case 'men':
      case 'women':
        return t(`${TP}.mobileOverlayMenOrWomenGenderTitle`, {
          defaultValue: "US {sizeCategory}'s Size",
          sizeCategory: getProductSizeCategory(sizeCategory),
        })
      default:
        return t(`${TP}.mobileOverlayGenderTitle`, {
          defaultValue: 'US {sizeCategory} Size',
          sizeCategory: getProductSizeCategory(sizeCategory),
        })
    }
  }

  useEffect(() => {
    if (isSelectorOpen && contentEl.current) {
      disableBodyScroll(contentEl.current)
    } else if (contentEl.current) {
      enableBodyScroll(contentEl.current)
    }
    return () => {
      if (contentEl.current) {
        disableBodyScroll(contentEl.current)
      }
    }
  }, [isSelectorOpen, contentEl.current])
  useEffect(() => setIsSelectorOpen(false), [activeProductModal])

  const getLowestPrice = () => {
    if (selectedSize) {
      if (selectedSize.new?.lowestPriceCents) {
        return selectedSize.new?.lowestPriceCents
      } else if (selectedSize.used?.lowestPriceCents) {
        return selectedSize.used?.lowestPriceCents
      }
    }

    return newLowestPrice || usedLowestPrice
  }

  const lowestPriceObj = getLowestPrice()
  const lowestPrice = lowestPriceObj
    ? formatMoneyObject(
        {
          amount: lowestPriceObj.localizedValue,
          amountUsdCents: lowestPriceObj.value,
          currency: currencyCode,
        },
        { showCents: false },
      )
    : undefined

  return (
    <>
      {underRetailLabel && (
        <div style={{ height: '26px' }}>
          <UnderRetailLabel>{underRetailLabel}</UnderRetailLabel>
          <IconWrapper>
            <SVGIcon name="underRetailArrow" size={6} margin="0 0 1px" />
          </IconWrapper>
        </div>
      )}
      {isButtonRowVisible && (
        <div>
          <SizeInputField data-qa={ProductQA.MobileSelectSizeButton} onClick={handleDropdownClick}>
            <span>
              {t(`${TP}.mobileSelectSizeButton`, {
                defaultValue: 'Size {size}',
                size: selectedSize?.size.display,
              })}
            </span>
            <Caret isOpen={isSelectorOpen} color="black" />
          </SizeInputField>
          {lowestPrice && (
            <Button
              buttonType="primary2"
              $fill
              onClick={handleDropdownClick}
              qaAttr={ProductQA.MobileBuyFromButton}
              data-testid="buy-new-button"
            >
              {t(`${TP}.mobileBuyFromButton`, {
                defaultValue: 'FROM {lowestPrice}',
                lowestPrice,
              })}
            </Button>
          )}
          {displayAllInclusiveCopy && (
            <FeesWrapper>
              <FeesLabel>
                {t(`${TP}.mobileNoAditionalFees`, 'No additional fees at checkout')}
              </FeesLabel>
            </FeesWrapper>
          )}
        </div>
      )}
      <MobileOverlay
        isOpen={isSelectorOpen}
        title={
          <MobileSizePickerModalTitle data-qa={ProductQA.MobileOverlayShoeTitle}>
            {productTemplate.name}
          </MobileSizePickerModalTitle>
        }
        onClose={handleDropdownClick}
      >
        <MobileOverlayPanel ref={contentEl} $extraMargin>
          {enableExperiments &&
            enableUnderRetailExperiment &&
            enablePricingCopyPlacementExperiment &&
            experimentState[regionSelectorPlacement]?.groupAssigned === 'test' && (
              <ShoppingRegionSelector />
            )}
          <SizeLabel data-qa={ProductQA.MobileOverlayGenderTitle}>
            {getSizeLabel(sizeCategory)}
          </SizeLabel>
          <SizeOptionWrapper data-qa={ProductQA.MobileSizePicker}>
            {sizePickerOptions.map((option) => (
              <SizeOptionTile
                isSelected={option.size.value === selectedSize?.size.value}
                onClick={() => handleSizeOptionClick(option)}
                key={option.size.value}
              >
                {option.size.display}
              </SizeOptionTile>
            ))}
          </SizeOptionWrapper>
        </MobileOverlayPanel>

        <PDPDivider />
        <MobileOverlayPanel>
          <BuyButtonRow />
        </MobileOverlayPanel>
      </MobileOverlay>
    </>
  )
}

const Caret = styled(RotatingCaret)`
  margin-top: 4px;
`

const IconWrapper = styled.div`
  display: inline-block;
  ${blinkStyles}
`

const FeesWrapper = styled.div`
  text-align: center;
  margin: 16px 0 40px;
`

const FeesLabel = styled.div`
  ${fonts.SUBTITLE_2};
  font-size: 12px;
  text-transform: none;
`

const UnderRetailLabel = styled.label`
  ${fonts.SUBTITLE_3};
  color: ${colors.FC2_RED};
  text-transform: none;
  font-weight: 400;
  margin-right: 5px;
  line-height: 120%;
`

export default SizePickerMobile
